import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { RiCloseLine } from 'react-icons/ri';

import {
  getDefaultDocPageId,
  getDocItemById,
  getDocNavFlat,
  getDocNavSections,
} from './documentation/docManifest';

function slugifyHeading(text) {
  return String(text)
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

function extractToc(markdown) {
  const entries = [];
  const lines = markdown.split('\n');
  for (const line of lines) {
    const match = /^(#{2,3})\s+(.+)$/.exec(line.trim());
    if (!match) {
      continue;
    }
    const level = match[1].length;
    const text = match[2].replace(/\s*\{#.+\}\s*$/, '').trim();
    entries.push({ id: slugifyHeading(text), text, level });
  }
  return entries;
}

const ProductDocumentationModal = ({
  open,
  onClose,
  isFreeForm = false,
}) => {
  const defaultPageId = getDefaultDocPageId(isFreeForm);
  const navSections = useMemo(
    () => getDocNavSections(isFreeForm),
    [isFreeForm],
  );
  const navFlat = useMemo(() => getDocNavFlat(isFreeForm), [isFreeForm]);

  const [activePageId, setActivePageId] = useState(defaultPageId);
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const activeItem = getDocItemById(activePageId, isFreeForm);

  useEffect(() => {
    if (open) {
      setActivePageId(defaultPageId);
    }
  }, [open, defaultPageId]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!getDocItemById(activePageId, isFreeForm)) {
      setActivePageId(defaultPageId);
    }
  }, [open, isFreeForm, activePageId, defaultPageId]);

  useEffect(() => {
    if (!open || !activeItem) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`${process.env.PUBLIC_URL || ''}/documentation/${activeItem.file}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load ${activeItem.file}`);
        }
        return res.text();
      })
      .then((text) => {
        if (!cancelled) {
          setMarkdown(text);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message || 'Could not load documentation.');
          setMarkdown('');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, activeItem]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const toc = useMemo(() => extractToc(markdown), [markdown]);

  const scrollToHeading = useCallback((id) => {
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  if (!open) {
    return null;
  }

  const title =
    activeItem?.title ||
    getDocItemById(defaultPageId, isFreeForm)?.title ||
    'Documentation';

  return createPortal(
    <div
      className="respark-product-docs"
      role="dialog"
      aria-modal="true"
      aria-label="Product documentation"
    >
      <div className="respark-product-docs__backdrop" onClick={onClose} aria-hidden />
      <div className="respark-product-docs__shell">
        <header className="respark-product-docs__header">
          <div className="respark-product-docs__brand">
            <span className="respark-product-docs__brand-label">Respark Help</span>
            <span className="respark-product-docs__brand-sep">/</span>
            <span className="respark-product-docs__brand-page">{title}</span>
          </div>
          <button
            type="button"
            className="respark-product-docs__close"
            aria-label="Close documentation"
            onClick={onClose}
          >
            <RiCloseLine size={22} aria-hidden />
          </button>
        </header>

        <div className="respark-product-docs__layout">
          <nav className="respark-product-docs__nav" aria-label="Documentation topics">
            {navSections.map((section) => (
              <div key={section.title} className="respark-product-docs__nav-section">
                <div className="respark-product-docs__nav-section-title">{section.title}</div>
                <ul className="respark-product-docs__nav-list">
                  {section.items.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={`respark-product-docs__nav-link${
                          item.id === activePageId ? ' respark-product-docs__nav-link--active' : ''
                        }`}
                        onClick={() => setActivePageId(item.id)}
                      >
                        {item.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <main className="respark-product-docs__main">
            <label className="respark-product-docs__mobile-nav">
              <span className="respark-product-docs__mobile-nav-label">Topic</span>
              <select
                className="respark-product-docs__mobile-nav-select"
                value={activePageId}
                aria-label="Documentation topic"
                onChange={(event) => setActivePageId(event.target.value)}
              >
                {navSections.map((section) => (
                  <optgroup key={section.title} label={section.title}>
                    {section.items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.title}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            <article className="respark-product-docs__article">
              {loading ? (
                <p className="respark-product-docs__status">Loading…</p>
              ) : error ? (
                <p className="respark-product-docs__status respark-product-docs__status--error">
                  {error}
                </p>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => {
                      const text = String(children);
                      return <h1 id={slugifyHeading(text)}>{children}</h1>;
                    },
                    h2: ({ children }) => {
                      const text = String(children);
                      return <h2 id={slugifyHeading(text)}>{children}</h2>;
                    },
                    h3: ({ children }) => {
                      const text = String(children);
                      return <h3 id={slugifyHeading(text)}>{children}</h3>;
                    },
                    a: ({ href, children }) => {
                      if (href?.endsWith('.md') || href?.startsWith('./')) {
                        const file = href.replace(/^\.\//, '');
                        const item = navFlat.find((i) => i.file === file);
                        if (item) {
                          return (
                            <button
                              type="button"
                              className="respark-product-docs__inline-link"
                              onClick={() => setActivePageId(item.id)}
                            >
                              {children}
                            </button>
                          );
                        }
                      }
                      return (
                        <a href={href} target="_blank" rel="noopener noreferrer">
                          {children}
                        </a>
                      );
                    },
                  }}
                >
                  {markdown}
                </ReactMarkdown>
              )}
            </article>
          </main>

          {toc.length > 0 && !loading && !error ? (
            <aside className="respark-product-docs__toc" aria-label="On this page">
              <div className="respark-product-docs__toc-title">On this page</div>
              <ul className="respark-product-docs__toc-list">
                {toc.map((entry) => (
                  <li
                    key={entry.id}
                    className={`respark-product-docs__toc-item respark-product-docs__toc-item--l${entry.level}`}
                  >
                    <button
                      type="button"
                      className="respark-product-docs__toc-link"
                      onClick={() => scrollToHeading(entry.id)}
                    >
                      {entry.text}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ProductDocumentationModal;
